import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { 
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  User,
  Phone,
  Mail,
  Truck,
  FileText,
  AlertCircle,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicApplicationPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    // Personal Info
    full_name: '',
    phone: '',
    email: '',
    country: 'US',
    
    // Driver Info
    cdl_type: 'Class A',
    truck_type: '',
    driver_type: 'lease_on', // 'owner_operator' or 'lease_on'
    preferred_lane: 'otr', // 'otr' or 'regional'
    years_experience: '',
    
    // Additional
    additional_notes: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    return formData.full_name && formData.phone && formData.email && formData.country && formData.years_experience;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fill in all required fields.');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      // Parse full name into first and last
      const nameParts = formData.full_name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const payload = {
        first_name: firstName,
        last_name: lastName,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        application_type: formData.driver_type,
        cdl_class: formData.cdl_type === 'Class A' ? 'A' : 'B',
        cdl_state: 'MI', // Default
        years_experience: formData.years_experience,
        preferred_routes: formData.preferred_lane,
        truck_year: '',
        truck_make: formData.truck_type,
        truck_model: '',
        has_own_truck: true,
        additional_notes: formData.additional_notes
      };

      const response = await fetch(`${API_URL}/api/public/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit application');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="application-page">
        <PublicNavbar />
        <div className="max-w-2xl mx-auto px-4 py-20">
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4" data-testid="confirmation-heading">
              Thank you for your submission.
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Your application has been received and will be reviewed. If approved, 
              you will be contacted with next steps.
            </p>
            <Link
              to="/"
              className="inline-flex items-center text-blue-900 font-medium hover:text-blue-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Home
            </Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="application-page">
      <PublicNavbar />

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Driver Application
          </h1>
          <p className="text-gray-600">
            Please complete the application below. Submission does not guarantee approval or engagement.
          </p>
          <p className="text-gray-600 mt-2">
            Approved applicants will receive next-step instructions.
          </p>
          <p className="text-sm text-gray-500 mt-3">
            Estimated completion time: 3–5 minutes.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Personal Information */}
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-900" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                data-testid="input-full-name"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="John Smith"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="input-phone"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="input-email"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country <span className="text-red-500">*</span>
              </label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                data-testid="select-country"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="MX">Mexico</option>
                <option value="OTHER">Other</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                BlueLock currently operates under U.S. authority and processes U.S.-based applicants at this time.
              </p>
            </div>

            {/* Driver Information */}
            <div className="flex items-center gap-3 mt-8 mb-6 pb-4 border-b border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-900" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Driver Information</h2>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CDL Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="cdl_type"
                  value={formData.cdl_type}
                  onChange={handleChange}
                  required
                  data-testid="select-cdl-type"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Class A">Class A</option>
                  <option value="Class B">Class B</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Truck Type
                </label>
                <input
                  type="text"
                  name="truck_type"
                  value={formData.truck_type}
                  onChange={handleChange}
                  data-testid="input-truck-type"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Freightliner Cascadia"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Driver Type <span className="text-red-500">*</span>
              </label>
              <div className="grid md:grid-cols-3 gap-4" data-testid="driver-type-radios">
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  formData.driver_type === 'owner_operator' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="driver_type"
                    value="owner_operator"
                    checked={formData.driver_type === 'owner_operator'}
                    onChange={handleChange}
                    className="sr-only"
                    data-testid="driver-type-owner-operator"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Owner-Operator</div>
                    <div className="text-xs text-gray-500 mt-1">Dispatch-Only · 8% or $400/wk</div>
                  </div>
                </label>
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  formData.driver_type === 'lease_on' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="driver_type"
                    value="lease_on"
                    checked={formData.driver_type === 'lease_on'}
                    onChange={handleChange}
                    className="sr-only"
                    data-testid="driver-type-lease-on"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Lease-On</div>
                    <div className="text-xs text-gray-500 mt-1">84% / 16% linehaul split</div>
                  </div>
                </label>
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  formData.driver_type === 'company_driver' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="driver_type"
                    value="company_driver"
                    checked={formData.driver_type === 'company_driver'}
                    onChange={handleChange}
                    className="sr-only"
                    data-testid="driver-type-company-driver"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Company Driver</div>
                    <div className="text-xs text-gray-500 mt-1">Company-provided truck</div>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Lane <span className="text-red-500">*</span>
              </label>
              <div className="grid md:grid-cols-2 gap-4">
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  formData.preferred_lane === 'otr' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="preferred_lane"
                    value="otr"
                    checked={formData.preferred_lane === 'otr'}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div>
                    <div className="font-medium text-gray-900">OTR</div>
                    <div className="text-sm text-gray-500">Over-the-Road</div>
                  </div>
                </label>
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  formData.preferred_lane === 'regional' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="preferred_lane"
                    value="regional"
                    checked={formData.preferred_lane === 'regional'}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Regional</div>
                    <div className="text-sm text-gray-500">Dedicated lanes</div>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Years of Experience <span className="text-red-500">*</span>
              </label>
              <select
                name="years_experience"
                value={formData.years_experience}
                onChange={handleChange}
                required
                data-testid="select-experience"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Experience</option>
                <option value="less_than_1">Less than 1 year</option>
                <option value="1_2">1-2 years</option>
                <option value="2_5">2-5 years</option>
                <option value="5_10">5-10 years</option>
                <option value="10_plus">10+ years</option>
              </select>
            </div>

            {/* Additional Notes */}
            <div className="flex items-center gap-3 mt-8 mb-6 pb-4 border-b border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-900" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Additional Information</h2>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Optional Notes
              </label>
              <textarea
                name="additional_notes"
                value={formData.additional_notes}
                onChange={handleChange}
                rows={4}
                data-testid="input-notes"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any additional information you'd like to share..."
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-application-btn"
              className="w-full inline-flex items-center justify-center px-8 py-3 bg-blue-900 text-white rounded-lg font-semibold hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
          </div>
        </form>

        {/* Disclaimer */}
        <p className="text-center mt-6 text-sm text-gray-500">
          Submission does not guarantee approval or engagement.
        </p>
      </div>

      <PublicFooter />
    </div>
  );
};

export default PublicApplicationPage;
